import type { ContentPage } from '../types'

const DB_NAME = 'spotlight-v1'
const STORE_NAME = 'projects'
const DB_VERSION = 1

export interface CacheEntry {
  projectId: string
  version: string
  pages: ContentPage[]
  cachedAt: number
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = (e) => {
      const db = (e.target as IDBOpenDBRequest).result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'projectId' })
      }
    }
    req.onsuccess = (e) => resolve((e.target as IDBOpenDBRequest).result)
    req.onerror = () => reject(req.error)
  })
}

export async function getCache(projectId: string): Promise<CacheEntry | null> {
  if (typeof indexedDB === 'undefined') return null
  try {
    const db = await openDB()
    return await new Promise<CacheEntry | null>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly')
      const req = tx.objectStore(STORE_NAME).get(projectId)
      req.onsuccess = () => resolve((req.result as CacheEntry) ?? null)
      req.onerror = () => reject(req.error)
      tx.oncomplete = () => db.close()
    })
  } catch {
    return null
  }
}

export async function setCache(
  projectId: string,
  version: string,
  pages: ContentPage[],
): Promise<void> {
  if (typeof indexedDB === 'undefined') return
  try {
    const db = await openDB()
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite')
      const entry: CacheEntry = { projectId, version, pages, cachedAt: Date.now() }
      const req = tx.objectStore(STORE_NAME).put(entry)
      req.onerror = () => reject(req.error)
      tx.oncomplete = () => { db.close(); resolve() }
    })
  } catch {
    // silently swallow — widget still works without persistence
  }
}

export async function clearCache(projectId: string): Promise<void> {
  if (typeof indexedDB === 'undefined') return
  try {
    const db = await openDB()
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite')
      const req = tx.objectStore(STORE_NAME).delete(projectId)
      req.onerror = () => reject(req.error)
      tx.oncomplete = () => { db.close(); resolve() }
    })
  } catch {}
}
